import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { UnassignedTicket } from '../data-models/unassigned-ticket.model';
import { AssignTicketRequest } from '../request-models/assign-ticket-request.model';
import { Page } from '../response-models/page.model';
import { isRecord } from '../utils/is-record';
import { countOrZero } from '../utils/wire-value-utils';

/**
 * Support tickets that could not be auto-matched to an organization: `/admin/support-tickets/unassigned[/count]`
 * and `/admin/support-tickets/:id/assign` (hub `admin-unassigned-tickets-controller.ts`). Paged with `limit`.
 * Failures reject with an `ApiError`.
 */
@Injectable({
  providedIn: 'root',
})
export class UnassignedTicketsService extends BaseService<UnassignedTicket> {
  constructor() {
    super('admin/support-tickets', UnassignedTicket);
  }

  /** GET /admin/support-tickets/unassigned?page&limit */
  getUnassignedTickets(query?: { page?: number; limit?: number }): Promise<Page<UnassignedTicket>> {
    return firstValueFrom(this.getPage({ path: 'unassigned', query: { page: query?.page, limit: query?.limit } }));
  }

  /** GET /admin/support-tickets/unassigned/count */
  getUnassignedCount(): Promise<number> {
    return firstValueFrom(this.request('GET', { path: ['unassigned', 'count'] }, data => countOrZero(isRecord(data) ? data['count'] : undefined)));
  }

  /** PUT /admin/support-tickets/:ticketId/assign, optionally linking a specific profile. */
  async assignTicket(ticketId: string, organizationId: string, profileId?: string): Promise<UnassignedTicket> {
    const body: AssignTicketRequest = profileId ? { organizationId, profileId } : { organizationId };
    const ticket = await firstValueFrom(this.request('PUT', { id: ticketId, path: 'assign' }, toTicketOrNull, body));
    if (!ticket) {
      throw new Error('Failed to assign ticket');
    }
    return ticket;
  }
}

function toTicketOrNull(data: unknown): UnassignedTicket | null {
  return data === null || data === undefined ? null : new UnassignedTicket(data);
}
